/* Выбор строки справочника — поиском, а не прокруткой списка.
 *
 * Обычный <select> годится, пока строк десяток: дисциплины, типы, коды. Объекты
 * и титулы проекта — не тот случай: их сотни, они называются похоже («2100»,
 * «2110», «2130»), и найти нужный в раскрытом списке нельзя ничем, кроме глаз.
 *
 * Поэтому здесь строка поиска над списком: ищет и по коду, и по наименованию,
 * список сужается на ходу. Выбранное показывается кнопкой — кодом и названием
 * сразу, чтобы «2100» не приходилось держать в голове.
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Icon } from '@/ds/icons'
import { Input } from './forms'
import type { Lang } from '@/i18n/lang'
import { t } from '@/i18n/lang'

export interface DictOption {
  code: string
  name?: string
  /** Приписка справа: объект титула, чтобы не открывать второй справочник. */
  note?: string
}

export function DictPicker({
  label,
  hint,
  value,
  options,
  onPick,
  lang,
  placeholder,
  emptyLabel,
  disabled = false,
  invalid = false,
}: {
  label?: ReactNode
  hint?: ReactNode
  value: string
  options: DictOption[]
  onPick: (code: string) => void
  lang: Lang
  placeholder?: string
  /** Что показывать, когда не выбрано ничего. */
  emptyLabel?: string
  disabled?: boolean
  /** Поле обязательно и пусто: рамка говорит об этом до нажатия «Создать». */
  invalid?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const box = useRef<HTMLDivElement>(null)

  /* Закрытие по клику мимо и по Escape: список перекрывает форму, и уйти из
     него надо тем же движением, каким уходят из любого меню. */
  useEffect(() => {
    if (!open) return
    const away = (e: MouseEvent) => {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false)
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false) }
    document.addEventListener('mousedown', away)
    document.addEventListener('keydown', esc)
    return () => {
      document.removeEventListener('mousedown', away)
      document.removeEventListener('keydown', esc)
    }
  }, [open])

  /* Фокус в поиске сразу: открыли — печатай, а не целься мышью. Поле ищется в
     самом списке: Input ссылку наружу не отдаёт. */
  useEffect(() => {
    if (open) box.current?.querySelector<HTMLInputElement>('input')?.focus()
  }, [open])

  const needle = q.trim().toLowerCase()
  const shown = useMemo(() => (needle
    ? options.filter((o) => (o.code + ' ' + (o.name ?? '') + ' ' + (o.note ?? '')).toLowerCase().includes(needle))
    : options), [options, needle])
  const picked = options.find((o) => o.code === value)

  return (
    <div style={{ position: 'relative' }} ref={box}>
      {label ? (
        <label style={{ display: 'block', marginBottom: 'var(--space-3)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: 'var(--weight-medium)', color: 'var(--text-body)' }}>{label}</label>
      ) : null}
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, width: '100%', height: 'var(--control-md)',
          padding: '0 var(--space-4)', textAlign: 'left', cursor: disabled ? 'not-allowed' : 'pointer',
          background: disabled ? 'var(--surface-sunken)' : 'var(--surface-card)',
          border: 'var(--border-hairline) solid ' + (invalid ? 'var(--danger-ink)' : 'var(--border-default)'),
          borderRadius: 'var(--radius-0)', font: 'var(--font-ui)', fontSize: 'var(--text-md)',
          color: picked ? 'var(--text-strong)' : 'var(--text-subtle)', opacity: disabled ? 0.7 : 1,
        }}
      >
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {picked
            ? picked.code + (picked.name ? ' — ' + picked.name : '')
            : (emptyLabel ?? t(lang, '— не выбран —', '— none —'))}
        </span>
        <Icon name="chevron-down" size={14} />
      </button>
      {hint ? (
        <div style={{ marginTop: 'var(--space-2)', font: 'var(--font-caption)', color: 'var(--text-subtle)' }}>{hint}</div>
      ) : null}
      {open ? (
        /* Список растёт от кнопки, которой его открыли, а не возникает готовым
           с тенью. Это не Radix: элемент монтируется по открытию, поэтому вход
           живёт прямо здесь — как у табличных поповеров (smart-table-pt). */
        <div style={{
          position: 'absolute', zIndex: 40, top: 'calc(100% + 4px)', left: 0, right: 0,
          background: 'var(--surface-card)', border: '1px solid var(--border-default)',
          borderRadius: 'var(--radius-sm)', boxShadow: 'var(--shadow-lg)', overflow: 'hidden',
          transformOrigin: 'top left',
          animation: 'tooltip-in var(--duration-normal) var(--ease-out)',
        }}>
          <div style={{ padding: 8, borderBottom: '1px solid var(--border-subtle)' }}>
            <Input size="sm" value={q} onChange={(e) => setQ(e.target.value)}
                   leading={<Icon name="search" size={13} />}
                   placeholder={placeholder ?? t(lang, 'Код или наименование…', 'Code or name…')} />
          </div>
          <div style={{ maxHeight: 260, overflow: 'auto' }}>
            {/* Пустой выбор — строкой списка, а не отдельной кнопкой: снять
                значение так же просто, как поставить. */}
            <button type="button" className="tdms-rail" onClick={() => { onPick(''); setOpen(false); setQ('') }}
                    style={row(value === '')}>
              <span style={{ color: 'var(--text-subtle)' }}>{emptyLabel ?? t(lang, '— не выбран —', '— none —')}</span>
            </button>
            {shown.map((o) => (
              <button key={o.code} type="button" className="tdms-rail"
                      onClick={() => { onPick(o.code); setOpen(false); setQ('') }}
                      style={row(o.code === value)}>
                <span style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-strong)', minWidth: 64 }}>{o.code}</span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{o.name}</span>
                {o.note ? <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>{o.note}</span> : null}
              </button>
            ))}
            {shown.length === 0 ? (
              <div style={{ padding: '10px 12px', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-subtle)' }}>
                {t(lang, 'Ничего не найдено', 'Nothing found')}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function row(active: boolean) {
  return {
    display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '7px 12px',
    border: 0, cursor: 'pointer', textAlign: 'left' as const,
    background: active ? 'var(--accent-soft)' : 'transparent',
    font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-body)',
  }
}
